import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import MuiTab from '@mui/material/Tab';
import MuiTabs from '@mui/material/Tabs';
import * as React from 'react';
import { FlexBox } from '../box';
import { Button, IconButton } from '../button';
import { Chip } from '../chip';
import { Divider } from '../divider';
import { styled } from '../styled';
import { usePaletteColor } from '../theme';
import { Typography } from '../typography';
const TabLabel = ({ item, isSelected, labelTextAlign }) => {
    if (item.icon) {
        return (_jsxs(FlexBox, { gap: 1, alignItems: "center", textAlign: labelTextAlign, width: item.iconWrapper?.width, justifyContent: item.iconWrapper?.justifyContent, children: [item.label, _jsx(item.icon, {})] }));
    }
    if (item.chipLabel) {
        return (_jsxs(FlexBox, { gap: 1, children: [item.label, _jsx(Chip, { color: isSelected ? 'primary' : 'default', label: item.chipLabel })] }));
    }
    return _jsx(_Fragment, { children: item.label });
};
const StyledFlexBox = styled(FlexBox)(({ $isSticky, $stickyTop, $stickyBackgroundColor, theme, $contentPadding, $isOrientationHorizontal }) => ({
    ...($isSticky && {
        height: 'fit-content',
        position: 'sticky',
        [$isOrientationHorizontal ? 'paddingBottom' : 'paddingTop']: theme.spacing($contentPadding ?? 1),
        top: $stickyTop,
        backgroundColor: $stickyBackgroundColor,
        zIndex: 1
    })
}));
const StyledMuiTabs = styled(MuiTabs)(({ theme, $isOrientationHorizontal }) => ({
    minHeight: 0,
    '.MuiTabs-scrollButtons': {
        color: theme.palette.action.active
    },
    ...(!$isOrientationHorizontal && {
        minWidth: 'fit-content'
    })
}));
const StyledMuiTab = styled(MuiTab)(({ $color, $selectedColor, $activeBackgroundColor, $labelTextAlign, theme }) => ({
    ...($labelTextAlign === 'left' && { alignItems: 'flex-start' }),
    ...($labelTextAlign === 'right' && { alignItems: 'flex-end' }),
    textTransform: 'none',
    borderRadius: 1,
    paddingTop: theme.spacing(0.75),
    paddingBottom: theme.spacing(0.75),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    minHeight: 0,
    lineHeight: theme.spacing(3),
    color: $color,
    '&[aria-selected="true"]': {
        color: $selectedColor,
        backgroundColor: $activeBackgroundColor
    }
}));
export const Tabs = React.forwardRef((props, ref) => {
    const { selectedTabIndex, onTabChange, sticky, labelTextAlign } = props;
    const [internalSelectedIndex, setInternalSelectedIndex] = React.useState(selectedTabIndex ?? 0);
    const isControlled = selectedTabIndex !== undefined;
    const activeTabIndex = isControlled
        ? selectedTabIndex
        : internalSelectedIndex;
    React.useEffect(() => {
        if (isControlled && selectedTabIndex !== internalSelectedIndex) {
            setInternalSelectedIndex(selectedTabIndex);
        }
    }, [selectedTabIndex, isControlled, internalSelectedIndex]);
    const orientation = props.orientation ?? 'horizontal';
    const isOrientationHorizontal = orientation === 'horizontal';
    const activeTabHeader = React.useMemo(() => props.items[activeTabIndex]?.header, [activeTabIndex, props.items]);
    const isSticky = Boolean(sticky);
    const stickyConfig = typeof sticky === 'object' ? sticky : {};
    const stickyTop = stickyConfig.top ?? 0;
    const stickyBackgroundColor = usePaletteColor(stickyConfig.backgroundColor) ?? 'initial';
    const { contentPadding } = stickyConfig;
    return (_jsxs(FlexBox, { ref: ref, "data-role": "tabs", gap: props.contentGap ?? 2, stack: isOrientationHorizontal, height: "100%", minHeight: 0, overflow: isSticky ? 'visible' : 'auto', stretch: isOrientationHorizontal, children: [_jsxs(StyledFlexBox, { gap: 0.75, stack: isOrientationHorizontal, "$isSticky": isSticky, "$stickyTop": stickyTop, "$stickyBackgroundColor": stickyBackgroundColor, "$contentPadding": contentPadding, "$isOrientationHorizontal": isOrientationHorizontal, children: [_jsx(StyledMuiTabs, { orientation: orientation, value: activeTabIndex, "$isOrientationHorizontal": isOrientationHorizontal, slotProps: { indicator: { hidden: true } }, onChange: (_e, newTabIndex) => {
                            if (!isControlled) {
                                setInternalSelectedIndex(newTabIndex);
                            }
                            onTabChange?.(newTabIndex);
                        }, variant: "scrollable", scrollButtons: "auto", children: props.items.map((item, index) => {
                            const LabelComponent = () => {
                                if (!item.chipLabel && !item.icon) {
                                    return item.label;
                                }
                                return (_jsx(TabLabel, { labelTextAlign: labelTextAlign, item: item, isSelected: activeTabIndex === index }));
                            };
                            return (_jsx(Tab, { "data-role": item['data-role'], label: _jsx(LabelComponent, {}), labelTextAlign: !isOrientationHorizontal
                                    ? labelTextAlign
                                    : undefined }, item['data-role'] ?? index));
                        }) }), isOrientationHorizontal && (_jsx(Divider, { orientation: "horizontal" }))] }), _jsxs(_Fragment, { children: [activeTabHeader && (_jsxs(FlexBox, { width: "100%", children: [_jsxs(FlexBox, { stretch: true, vAlign: "center", children: [_jsx(Typography, { variant: "h6", children: activeTabHeader.title }), Boolean(activeTabHeader.info) && (_jsx(IconButton, { size: "small", tooltip: activeTabHeader.info, icon: "IconInfo" }))] }), _jsx(FlexBox, { gap: 1, children: activeTabHeader.actions?.map((action, index) => {
                                    return (_jsx(Button, { variant: "contained", onClick: action.onClick, children: action.title }, index));
                                }) })] })), props.items.map((item, index) => {
                        const isActive = index === activeTabIndex;
                        if (!isActive && !item.keepMounted) {
                            return null;
                        }
                        const ContentComponent = item.content;
                        const hideTabContent = !isActive && item.keepMounted;
                        return (_jsx(FlexBox, { "data-role": "tab-content", minHeight: 0, ...(hideTabContent && {
                                height: '100%',
                                position: 'absolute',
                                visibility: 'hidden',
                                pointerEvents: 'none'
                            }), stretch: true, children: _jsx(FlexBox, { width: "100%", children: _jsx(ContentComponent, {}) }) }, item['data-role'] ?? index));
                    })] })] }));
});
Tabs.displayName = 'Tabs';
const Tab = React.forwardRef(({ labelTextAlign, ...props }, ref) => {
    const color = usePaletteColor('components.tab.primary.font');
    const selectedColor = usePaletteColor('components.tab.primary.font-active');
    const backgroundColor = usePaletteColor('components.tab.primary.bg-active');
    return (_jsx(StyledMuiTab, { ref: ref, ...props, "$color": color, "$selectedColor": selectedColor, "$activeBackgroundColor": backgroundColor, "$labelTextAlign": labelTextAlign }));
});
